import { loadPageScripts } from './pages';
import { getSmartPrice, renderSmartPriceHtml } from './products';
import { getObj, setObj, updateObj, removeObj } from './localStorage';
import { init as initCookies } from './cookies';
import { onReady } from './util';
import {
  displayDevModeBanner,
  getUrlParameter,
  removeUrlParameter,
  updateUrlParameter,
} from './window';
import { retrieveUtmParameters, trackCheckout, createFauxOrder } from './ecommerceTracking';
import {
  initializeCountrySelector,
  renderCountryModal,
  initializeLanguageSelector,
} from './pages/countryModal';
import { shouldShowCountryModal, listenForCountryPicks } from './country';

const { version } = require('../package.json');

/* eslint-disable no-undef */
const environment = typeof __KLIKKIE_ENV__ !== 'undefined' ? __KLIKKIE_ENV__ : 'prod';
/* eslint-enable no-undef */
setObj('env', { env: environment });
const discountCode =
  getUrlParameter('couponCode') || getObj('discountCode').discountCode || undefined;
console.log('discountCode is', discountCode);

if (discountCode) {
  const currentTime = new Date().getTime();
  const discountCodeObj = getObj('discountCode');

  const savedTime = discountCodeObj.time
    ? new Date(parseInt(discountCodeObj.time, 10)).getTime()
    : currentTime;
  if (currentTime - savedTime < 24 * 60 * 60 * 1000 || getUrlParameter('couponCode')) {
    // Active coupon: suppress all generic promo banners so the green coupon
    // banner is the only one users see. Two component systems exist —
    // .kk-banner (banner.njk / banner-subscription / banner-photobook) and
    // .discountcomponent (discount-banner.njk, e.g. NL Mother's Day campaign);
    // both get hidden. Stays hidden even after the green banner is dismissed —
    // user already holds the discount, other promo reminders are moot.
    document.querySelectorAll('.kk-banner, .discountcomponent').forEach((el) => {
      el.style.display = 'none';
    });

    updateObj('discountCode', { discountCode, time: currentTime.toString() });
    updateUrlParameter('couponCode', discountCode);
    const discountBanner = document.querySelector('.discountbanner');
    const couponField = document.querySelector('.couponfield');
    if (discountBanner) {
      couponField.textContent = discountCode;
      // Persist dismissal by coupon code: hide whenever the current coupon
      // matches the one the user dismissed. A different coupon auto-invalidates
      // (shows again), and closing the tab clears sessionStorage (shows again).
      const dismissedKey = 'discountBannerDismissed';
      const dismissedCoupon = sessionStorage.getItem(dismissedKey);
      if (dismissedCoupon !== discountCode) {
        discountBanner.style.display = 'flex';
      }
      const closeBtn = discountBanner.querySelector('.discountbanner-close');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => {
          // Full clear: dismissing the banner means the user opted out
          // of this coupon. Wipe the URL param, localStorage cache, and
          // session dismissal flag so the banner doesn't come back on
          // refresh from any source. The .has-coupon class added by
          // head-styles.njk's inline script (suppresses .kk-banner) is
          // also removed so the regular promos can show again.
          discountBanner.style.display = 'none';
          removeUrlParameter('couponCode');
          removeObj('discountCode');
          sessionStorage.removeItem(dismissedKey);
          document.documentElement.classList.remove('has-coupon');
          // re-show any promo banners that the inline head script /
          // klikkie.js had hidden (they were just set to display:none
          // inline by the active-coupon branch above).
          document.querySelectorAll('.kk-banner, .discountcomponent').forEach((el) => {
            el.style.display = '';
          });
        });
      }
    }
  }
}

console.log(`klikkie.js version v${version}`);
console.log(`environment is ${environment}`);

// PDP view_item — fire through trackCheckout so user_data (email, phone,
// external_id, fbc/fbp, click IDs) is attached. Without this, GTM's Meta
// Pixel ViewContent tag fires with no advanced-matching params → EMQ ~4.1.
// PDP templates emit `window.__klikkieViewItem` synchronously in the head
// before this bundle defers; we read it once on bundle init.
if (typeof window !== 'undefined' && window.__klikkieViewItem) {
  try {
    const v = window.__klikkieViewItem;
    const viewOrder = createFauxOrder({
      sku: v.sku,
      units: 1,
      type: v.collection || 'product',
      name: v.name,
      unitPrice: Math.round((Number(v.price) || 0) * 100),
      currency: v.currency || 'EUR',
      countryCode: v.countryCode || 'NL',
      collection: v.collection || null,
      slug: v.slug || null,
      size: v.size || null,
    });
    trackCheckout('view_item', viewOrder);
  } catch (e) {
    console.warn('view_item fire failed', e);
  }
}

// The markup each `.smartprice` element was built with, kept so a re-bind
// still has the real fallback. Without it the second pass would hand
// renderSmartPriceHtml the *first* pass's output, and an element whose type
// is missing from the new country's price would keep quoting the old one.
const originalSmartPriceHtml = new WeakMap();

/**
 * Fill every `.smartprice` element for a country. Runs at boot with no
 * country (getSmartPrice resolves one) and again on `klikkie:country`, which
 * passes the new one explicitly.
 */
export const bindSmartPrices = async (forCountry = null) => {
  const smartPrices = document.getElementsByClassName('smartprice');
  const smartPricesArray = Array.from(smartPrices);

  const pricePromises = smartPricesArray.map(async (smartPrice) => {
    const { sku } = smartPrice.dataset;
    const { currency } = smartPrice.dataset;

    if (sku) {
      if (!originalSmartPriceHtml.has(smartPrice)) {
        originalSmartPriceHtml.set(smartPrice, smartPrice.innerHTML);
      }
      const prices = await getSmartPrice(sku, currency, forCountry);

      // KL-15115: monthly prices render sale presentation (struck-through
      // original + discounted) when the API flags a subscription promotion;
      // everything else keeps the plain value it always had.
      // eslint-disable-next-line no-param-reassign
      smartPrice.innerHTML = renderSmartPriceHtml(
        smartPrice.dataset.type,
        prices,
        originalSmartPriceHtml.get(smartPrice)
      );

      // KL-15450: an element may opt in with `data-visible-if="<flag>"` to
      // stay hidden unless that boolean on the resolved price is true. Used
      // for pre-promotion prices, which are only meaningful while a
      // promotion runs: on a product without prepaid months `priceOriginal`
      // equals `price`, so rendering it unconditionally would strike through
      // the very amount shown next to it. Markup ships `hidden`, so no
      // promotion and no JS at all both fail safe to nothing shown.
      const { visibleIf } = smartPrice.dataset;
      if (visibleIf) {
        // eslint-disable-next-line no-param-reassign
        smartPrice.hidden = !prices[visibleIf];
      }
    }
  });

  await Promise.all(pricePromises);
};

export const loadDynamicContent = async () => {
  try {
    // Fill out all smart price elements with the right price
    await bindSmartPrices();

    // Prices are country-dependent, so they follow the country rather than
    // waiting for the next page load. The country is taken from the event —
    // see the warning on getSmartPrice's `forCountry`.
    document.addEventListener('klikkie:country', (event) => {
      const country = event?.detail?.country;
      if (country) bindSmartPrices(country);
    });

    // Generate country selector
    const countrySelector = document.getElementById('countrySelector');
    if (countrySelector) {
      initializeCountrySelector();
    }

    // Generate language selector
    const languageSelector = document.getElementById('languageSelector');
    if (languageSelector) {
      initializeLanguageSelector();
    }
  } catch (error) {
    console.warn('Failed to load dynamic content', { error });
  }
};

// Initialize country detection with modal fallback
export const initCountryDetection = async () => {
  try {
    // Attach before resolving: a visitor can pick from the nav chip while the
    // GeoIP lookup below is still in flight, and their pick must win.
    listenForCountryPicks();

    const showModal = await shouldShowCountryModal();

    if (showModal) {
      // Country not detected or not supported - show modal
      console.log('Country not detected or not supported, showing country selection modal');
      await renderCountryModal();
    }
  } catch (error) {
    console.warn('Failed to initialize country detection', { error });
  }
};

// Lazy-load heavy dependencies (Firebase, Sentry, auth) only when needed.
// These are deferred so the main bundle stays lightweight for all pages.
//
// IMPORTANT: Firebase must be initialized BEFORE page scripts run, because
// funnel pages (checkout, details) call getAuth() on init. If page scripts
// race ahead of initFirebase(), they crash with "No Firebase App '[DEFAULT]'".
// This is especially critical on payment redirect returns (iDEAL, 3DS) where
// the checkout page immediately processes the redirectResult on load.
const initHeavyDepsAndPageScripts = async () => {
  // 1. Firebase + Sentry init (lazy — not needed for smartprice/country/cookies)
  try {
    const [{ initFirebase }, { initSentry }] = await Promise.all([
      import(/* webpackChunkName: "firebase-app" */ './firebaseApp'),
      import(/* webpackChunkName: "sentry" */ './sentry'),
    ]);
    initFirebase();
    initSentry();
  } catch (error) {
    console.warn('Failed to initialize Firebase/Sentry', { error });
  }

  // 2. Auth + logout link (only needed on funnel/membership pages, but loaded
  //    before page scripts so getAuth() is safe to call).
  // KL-14085: await authSetupAndLogin() so page scripts (incl. trackPurchase →
  // getOrder) don't fire before signInWithCustomToken resolves. Without the
  // await, the iDEAL `?token=` re-auth races with the first API call, the
  // request ships without an Authorization header, and klikkie-orders returns
  // 403 (visible in prod for ~4.5% of iOS Safari conversions). authStateReady
  // in api.js getToken resolves the moment Firebase restores persisted state
  // (null in Safari ITP cases) and doesn't wait for our pending sign-in.
  try {
    const { authSetupAndLogin, identifyVisitor } = await import(
      /* webpackChunkName: "user" */ './user'
    );
    await import(/* webpackChunkName: "logoutLink" */ './logoutLink');
    await authSetupAndLogin();
    // KL-15896: the page's one guest start (it replaces the separate
    // anon-auth.js bundle, a second copy of Firebase that started its own
    // guest). Not awaited: page scripts that need the user share this start.
    identifyVisitor();
  } catch (error) {
    console.warn('Failed to initialize auth', { error });
  }

  // 3. Page scripts — Firebase is guaranteed initialized above
  await loadPageScripts();
};

// --- Global init (runs on ALL pages, lightweight) ---
retrieveUtmParameters();
onReady(loadDynamicContent);
onReady(initCookies);
onReady(initCountryDetection);
onReady(displayDevModeBanner);

// --- Heavy init THEN page scripts (single callback = guaranteed sequential) ---
onReady(initHeavyDepsAndPageScripts);

export { clearStorage, getObj, updateObj, setObj } from './localStorage';

// Re-exports for external consumers — these trigger lazy loading when actually used
export const createUser = async (...args) => {
  const mod = await import('./user');
  return mod.createUser(...args);
};
export const updateUser = async (...args) => {
  const mod = await import('./user');
  return mod.updateUser(...args);
};
export const updateUserPassword = async (...args) => {
  const mod = await import('./user');
  return mod.updateUserPassword(...args);
};

export const getOrder = async (...args) => {
  const mod = await import('./order');
  return mod.getOrder(...args);
};
export const createOrder = async (...args) => {
  const mod = await import('./order');
  return mod.createOrder(...args);
};
export const updateOrder = async (...args) => {
  const mod = await import('./order');
  return mod.updateOrder(...args);
};
export const addProduct = async (...args) => {
  const mod = await import('./order');
  return mod.addProduct(...args);
};
export const removeProduct = async (...args) => {
  const mod = await import('./order');
  return mod.removeProduct(...args);
};
export const addAddress = async (...args) => {
  const mod = await import('./order');
  return mod.addAddress(...args);
};
export const verifyCoupon = async (...args) => {
  const mod = await import('./order');
  return mod.verifyCoupon(...args);
};

export { removeDevMode } from './window';

export const logError = async (...args) => {
  const mod = await import('./sentry');
  return mod.logError(...args);
};
